import React, { useState, useEffect } from "react";
import { Phone } from "lucide-react";
import { AuthContainer } from "./AuthContainer";
import { useAuth } from "./AuthContext";
import { apiGet, apiPatch, apiPost } from "../services/api.service";
import { showError } from "../services/notification.service";
import { useTranslation } from "../i18n/TranslationContext";

export function PhoneNumber() {
	const { t } = useTranslation();
	const [phone, setPhone] = useState("");
	const [originalPhone, setOriginalPhone] = useState("");
	const [errors, setErrors] = useState<{ phone?: string }>({});
	const [isLoading, setIsLoading] = useState(false);
	const { setCurrentStep, updateAuthData, authData } = useAuth();

	// Fetch existing phone number on mount - only for logged-in users
	useEffect(() => {
		// First check if phone number is already in authData (from invitation flow)
		if (authData.phoneNumber) {
			setPhone(authData.phoneNumber);
			setOriginalPhone(authData.phoneNumber);
			return;
		}

		const fetchUserProfile = async () => {
			// Only fetch if we have both extraId AND an access token (logged-in user)
			const hasToken = localStorage.getItem("accessToken");
			if (authData.extraId && hasToken) {
				try {
					const response = await apiGet(`/extras/${authData.extraId}`);

					if (response.success && response.data?.mobile_number) {
						setPhone(response.data.mobile_number);
						setOriginalPhone(response.data.mobile_number);
					}
				} catch (err) {
					console.error("Failed to fetch user profile:", err);
					// Not critical - user can still enter phone number
				}
			}
		};

		fetchUserProfile();
	}, [authData.extraId, authData.phoneNumber]);

	const validateForm = () => {
		const newErrors: { phone?: string } = {};
		const phoneTrimmed = phone.trim();

		if (!phoneTrimmed) {
			newErrors.phone = t("phoneNumber.required");
		}
		// else if (!phoneTrimmed.startsWith("+")) {
		// 	newErrors.phone =
		// 		"Bitte geben Sie die Telefonnummer mit Ländervorwahl ein (z.B. +49 für Deutschland).";
		// } else if (phoneTrimmed.length < 10) {
		// 	newErrors.phone =
		// 		"Die Telefonnummer ist zu kurz. Bitte geben Sie eine vollständige Nummer mit Ländervorwahl ein.";
		// } else if (!/^\+\d[\d\s\-\(\)]*$/.test(phoneTrimmed)) {
		// 	newErrors.phone =
		// 		"Die Telefonnummer enthält ungültige Zeichen. Verwenden Sie nur Zahlen, Leerzeichen und Bindestriche.";
		// } else {
		// 	// Check for excessively long numbers
		// 	const digitsOnly = phoneTrimmed.replace(/[^\d]/g, "");
		// 	if (digitsOnly.length > 15) {
		// 		newErrors.phone =
		// 			"Die Telefonnummer ist zu lang. Bitte überprüfen Sie Ihre Eingabe.";
		// 	}
		// }

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateForm()) return;

		setIsLoading(true);
		setErrors({});

		try {
			// Only update if phone number changed AND we have auth token (existing user)
			const hasToken = localStorage.getItem("accessToken");
			if (phone !== originalPhone && authData.extraId && hasToken) {
				const response = await apiPatch(`/extras/${authData.extraId}`, {
					mobile_number: phone,
				});

				if (!response.success) {
					setErrors({ phone: t("phoneNumber.errors.general") });
					return;
				}
			}

			// Get the email verification token from sessionStorage
			const emailVerificationToken = sessionStorage.getItem(
				"emailVerificationToken"
			);

			if (!emailVerificationToken) {
				setErrors({
					phone:
						"Sitzung abgelaufen. Bitte starten Sie den Anmeldevorgang neu.",
				});
				return;
			}

			// Send SMS verification before navigating
			const smsResponse = await apiPost(
				"/verification/sms/send",
				{
					phoneNumber: phone,
					emailVerificationToken,
				},
				{ notification: { showError: true } }
			);

			if (smsResponse.success) {
				// Store the send time for rate limiting
				sessionStorage.setItem(`sms_last_sent_${phone}`, Date.now().toString());

				// Store phone in auth context and proceed
				updateAuthData({ phone });
				setCurrentStep(3); // Go to phone verification
			} else {
				// Handle errors
				const errorMessage =
					smsResponse.message ||
					smsResponse.error ||
					smsResponse.data?.message ||
					"";
				if (
					errorMessage &&
					(errorMessage.toLowerCase().includes("expired") ||
						errorMessage.toLowerCase().includes("email verification") ||
						errorMessage.toLowerCase().includes("token"))
				) {
					setErrors({
						phone:
							"Sitzung abgelaufen. Bitte starten Sie den Anmeldevorgang neu.",
					});
					// Clear the invalid token
					sessionStorage.removeItem("emailVerificationToken");
				} else {
					setErrors({ phone: errorMessage || t("phoneNumber.errors.general") });
				}
			}
		} catch (err: any) {
			// Check if it's a rate limit error (429)
			if (err?.status === 429 && err?.data?.remainingSeconds) {
				setErrors({
					phone: `Bitte warten Sie ${err.data.remainingSeconds} Sekunden, bevor Sie einen neuen Code anfordern.`,
				});
			} else {
				// Check if it's a token expiration/verification required error
				const errorMessage = err?.message || err?.data?.message || "";
				if (
					errorMessage &&
					(errorMessage.toLowerCase().includes("expired") ||
						errorMessage.toLowerCase().includes("email verification") ||
						errorMessage.toLowerCase().includes("token"))
				) {
					setErrors({
						phone:
							"Sitzung abgelaufen. Bitte starten Sie den Anmeldevorgang neu..",
					});
					// Clear the invalid token
					sessionStorage.removeItem("emailVerificationToken");
				} else {
					setErrors({ phone: errorMessage || t("phoneNumber.errors.general") });
				}
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<AuthContainer showBack={false} showProgress>
			<div className="text-center mb-8">
				<h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-8 leading-tight">
					{t("phoneNumber.title")}
				</h1>

				{/* Show indication if user came from Google OAuth */}
				{authData.authMethod === "google" && authData.googleProfile && (
					<div className="mb-6 p-4 bg-[var(--ucm-light-blue)] rounded-xl">
						<p className="text-sm text-[var(--ucm-text-medium)]">
							{t("phoneNumber.googleSubtitle")}{" "}
							<span className="font-medium text-[var(--ucm-text-dark)]">
								{authData.googleProfile.email}
							</span>
						</p>
					</div>
				)}
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				<div>
					<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
						{t("phoneNumber.label")}
					</label>
					<div className="relative">
						<input
							type="tel"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
							className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
								errors.phone
									? "border-[var(--ucm-error-red)]"
									: "border-[var(--ucm-border-light)]"
							}`}
							placeholder={t("phoneNumber.placeholder")}
						/>
						<Phone className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5" />
					</div>
					{errors.phone && (
						<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
							{errors.phone}
						</p>
					)}
				</div>

				<button
					type="submit"
					disabled={isLoading}
					className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] disabled:bg-[var(--ucm-text-light)] disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2"
				>
					{isLoading ? t("phoneNumber.updating") : t("phoneNumber.continue")}
				</button>
			</form>
		</AuthContainer>
	);
}
